import { Stack, Typography, TextField, Grid, Paper, MenuItem, Button, IconButton, CircularProgress } from "@mui/material";
import { useForm, Controller } from "react-hook-form"; // Import Controller
import { yupResolver } from "@hookform/resolvers/yup";
import { basicDetailsFormSchema } from "../../schemas/basicDetailsFormSchema";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from 'react-hot-toast';
import axios from "axios";

import { CONFIG } from "../../../../../config";

// ICONS
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';
import { getAxiosErrorMessage } from "../../../../../utility/axios-error-message";
// import CloseIcon from '@mui/icons-material/Close'; // Removed unused import

const API_HOST = CONFIG.modules["crm"].host;

const BasicDetailsForm = ({ caseData, updateCaseData }) => {
  const {
    Districts
  } = useSelector((state) => state.user || {});

  const districts = useMemo(() => {
    if (Districts) {
      return Districts.map((district) => ({
        label: district.name,
        value: district._id.toString(),
      }));
    } else {
      return [];
    }
  }, [Districts]);



  const [updateMode, setUpdateMode] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  // Add control to useForm destructuring
  const { register, handleSubmit, formState: { errors }, setValue, getValues, control } = useForm({
    resolver: yupResolver(basicDetailsFormSchema),
    defaultValues: {
      name: caseData?.name || "",
      contact: caseData?.contact || "",
      district: caseData?.districtId?.toString() || "",
      motherID: caseData?.motherID || "", // Add motherID default value
    }
  });

  useEffect(() => {
    if (caseData) {
      setValue("name", caseData.name || "");
      setValue("contact", caseData.contact || "");
      setValue("district", caseData.districtId?.toString() || "");
      setValue("motherID", caseData.motherID || ""); // Add motherID setValue
    }
  }, [caseData, setValue, districts]);

  const handleUpdate = async (data) => {
    // Need to check if the values have changed or not
    const values = getValues();
    if (values.name === caseData?.name && values.contact === caseData?.contact && values.district === caseData?.districtId?.toString() && values.motherID === caseData?.motherID) {

      toast.error("No changes to update", {
        duration: 1200,
        icon: "🔴",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
        position: "top-center",
      });

      return;
    }

    setUpdateLoading(true);
    try {
      // we need to update district to districtId
      data.districtId = data.district;
      delete data.district;

      const response = await axios.put(`${API_HOST}/case/${caseData?.motherID}`, data);

      if (response.status === 200) {
        toast.success("Case details updated successfully", {
          duration: 1200,
          icon: "🟢",
        });

        updateCaseData(response.data.case);
      }

    } catch (error) {
      toast.error(getAxiosErrorMessage(error), {
        duration: 1200,
        icon: "🔴",
        style: {
          borderRadius: "10px",
          background: "#333",
          color: "#fff",
        },
        position: "top-center",
      });
    } finally {
      setUpdateLoading(false);
    }
  }

  return (
    <Stack direction={"column"} spacing={1} px={2} py={1}>
      <Stack component={Paper} elevation={2} direction={"column"} spacing={1} px={2} py={2}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
          <Typography variant="h6" gutterBottom>
            Basic Details
          </Typography>

          <Stack>
            <IconButton
              disabled={updateLoading}
              color="primary"
              onClick={() => setUpdateMode(!updateMode)}
            >
              {updateMode ? <DoneIcon /> : <EditIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Removed extra nested Stack component={"Paper"} */}
        {/* Simplified form Stack direction */}
        <Stack component={"form"} onSubmit={handleSubmit(handleUpdate)} direction={"column"} spacing={2}>
          <Grid
            container
            rowGap={2} // Match CrmRelatedPropertiesForm spacing
            columnSpacing={{
              xs: 0,
              md: 2
            }} sx={{ // Match CrmRelatedPropertiesForm padding
              pr: {
                xs: 0,
                md: 2
              }
            }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Name"
                {...register("name")}
                error={!!errors.name}
                helperText={errors.name?.message}
                disabled={!updateMode}
              />

            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Contact"
                {...register("contact")}
                error={!!errors.contact}
                helperText={errors.contact?.message}
                disabled={!updateMode}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mother ID"
                {...register("motherID")}
                error={!!errors.motherID}
                helperText={errors.motherID?.message}
                disabled={!updateMode}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              {/* Use Controller for Select */}
              <Controller
                name="district"
                control={control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="District" // Simplified label
                    {...field}
                    error={!!errors.district}
                    helperText={errors.district?.message}
                    disabled={!updateMode}
                  // defaultValue removed, handled by useForm's defaultValues
                  >
                    {districts.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
          </Grid>
          {/* Conditional rendering for Update button */}
          {updateMode && (
            // Simplified button Stack layout
            <Stack direction={"row"} justifyContent={"flex-end"}>
              <Button type="submit" variant="contained" color="primary" disabled={updateLoading} sx={{ height: "100%" }}>
                {updateLoading ? <CircularProgress size={20} /> : "Update"}
              </Button>
            </Stack>
          )}
        </Stack>
        {/* Removed extra closing Stack tag */}
      </Stack>
    </Stack>
  )
}

export default BasicDetailsForm;
